import { z } from 'zod'
import { oc } from '@orpc/contract'
import {
  billingEntrySchema,
  billingItemSchema,
  billingSuggestionSchema,
} from '../soap-note-payload.contract'

const visitBillingEntrySchema = billingEntrySchema.extend({
  billingItems: z.array(
    billingItemSchema.extend({
      /** PD-1958 — the Claim points at a Condition that does not exist (a dangling `temp-*`
       *  reference); no `dx`. The biller removes or replaces the row before a Save. */
      unavailable: z.boolean().optional(),
    }),
  ),
})

/**
 * The billing READ for one encounter (PD-1871 / PD-1872) — what the Billing page's code
 * editor opens on. The note is resolved the way Lock resolves it (direct link, then the
 * sibling Provenance for notes written before `context.encounter` existed), the lines come
 * off the encounter's single Claim with their diagnoses resolved and the note's draft blob
 * as the fallback, and the rendering provider is the one the charge quote prices against.
 * `documentReferenceId: null` means the encounter has no progress note at all. The write
 * stays `visitUpdateBilling`.
 */
export const visitBillingResponseSchema = z.object({
  documentReferenceId: z.string().nullable(),
  docStatus: z.enum(['preliminary', 'final', 'amended']).nullable(),
  patientId: z.string(),
  /** The encounter's Practitioner participant, else the note's authenticator; null when neither exists. */
  renderingProviderId: z.string().nullable(),
  /** `Encounter.period.start` — the date the claim's code set is judged by (PD-2014). */
  dateOfService: z.string().nullable(),
  entries: z.array(visitBillingEntrySchema),
  /** PD-1958 — the Claim has no diagnoses; the lines show the note's, which a Save writes. */
  diagnosesFromNote: z.boolean().optional(),
  /** PD-1958 — the Claim's Conditions could not be read (a transient failure); a Save now could drop one. */
  diagnosesUnavailable: z.boolean().optional(),
  billingSuggestion: billingSuggestionSchema.nullable(),
})
export type VisitBillingResponse = z.infer<typeof visitBillingResponseSchema>

export const visitBillingGet = oc
  .route({ method: 'GET', path: '/v2/visits/{encounterId}/billing' })
  .input(z.object({ encounterId: z.string() }))
  .output(visitBillingResponseSchema)
